import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import Moment from "moment";
import { IconDownload, IconColumns } from "@tabler/icons";
import { Button, EmptyState } from "./core";
import { inr, count, dateTime, timeAgo, csvSafe } from "./format";

/* ------------------------------------------------------------------ */
/* ag-grid helpers                                                     */
/* ------------------------------------------------------------------ */

const numberComparator = (a, b) => (Number(a) || 0) - (Number(b) || 0);
const dateComparator = (a, b) => (a ? Moment(a).valueOf() : 0) - (b ? Moment(b).valueOf() : 0);

// Column types for colDef.type (a string or an array):
//   number  right aligned, tabular figures, numeric sort
//   money   number + "₹ 1,234.50" (the CSV keeps the raw value)
//   date    "3 Mar 2026, 4:05 PM", sorted by time
//   actions buttons column: no sort/filter/menu and never exported to CSV
//   noCsv   any other column to leave out of the CSV
export const GRID_COLUMN_TYPES = {
  number: { cellClass: "pa-num pa-cell-right", headerClass: "pa-head-right", comparator: numberComparator, filter: "agNumberColumnFilter" },
  money: {
    cellClass: "pa-num pa-cell-right",
    headerClass: "pa-head-right",
    comparator: numberComparator,
    filter: "agNumberColumnFilter",
    valueFormatter: (p) => (p.value === null || p.value === undefined || p.value === "" ? "" : inr(p.value)),
  },
  date: { comparator: dateComparator, valueFormatter: (p) => dateTime(p.value), minWidth: 150 },
  actions: { sortable: false, filter: false, resizable: false, suppressMenu: true, cellClass: "pa-actions-cell" },
  noCsv: {},
};

export const gridDefaults = {
  defaultColDef: { resizable: true, sortable: true, filter: true, minWidth: 90 },
  columnTypes: GRID_COLUMN_TYPES,
  rowHeight: 46,
  headerHeight: 42,
  animateRows: true,
  pagination: true,
  paginationPageSize: 50,
  enableCellTextSelection: true,
  suppressCellSelection: true,
  overlayNoRowsTemplate: '<div class="pa-grid-overlay">No rows match these filters</div>',
  overlayLoadingTemplate: '<div class="pa-grid-overlay"><span class="pa-spinner" aria-hidden="true"></span> Loading…</div>',
  // The kit adds colDef keys of its own (csvValue), which ag-grid 26 would
  // report as "invalid colDef property" on every grid that uses them.
  suppressPropertyNamesCheck: true,
};

const typesOf = (def) => (Array.isArray(def.type) ? def.type : def.type ? [def.type] : []);
const leafDefs = (defs, out = []) => {
  (defs || []).forEach((d) => (d.children ? leafDefs(d.children, out) : out.push(d)));
  return out;
};

// Downloads the grid as CSV. Columns without a field/valueGetter (buttons)
// and columns typed "actions" or "noCsv" are left out; pinned total rows are
// skipped. colDef.csvValue(data) overrides a cell's exported value.
// Options:
//   columnKeys    the exact columns (in order), hidden ones included
//   includeHidden colIds of hidden (detail) columns to export as well
//   exactName     use fileName as given (default: "<name>-YYYY-MM-DD-HHmm.csv")
//   formulaSafe   neutralise text a spreadsheet would run as a formula (csvSafe)
export function exportCsv(gridApi, fileName = "export", { columnKeys, includeHidden, exactName = false, formulaSafe = false } = {}) {
  if (!gridApi) return;
  let keys = columnKeys;
  if (!keys) {
    const extra = (includeHidden || []).map(String);
    try {
      keys = leafDefs(gridApi.getColumnDefs())
        .filter((d) => (!d.hide || extra.includes(String(d.colId || d.field))) && (d.field || d.valueGetter) && !typesOf(d).some((t) => t === "actions" || t === "noCsv"))
        .map((d) => d.colId || d.field);
    } catch (e) {
      keys = undefined;
    }
  }
  const safe = formulaSafe ? csvSafe : (v) => v;
  gridApi.exportDataAsCsv({
    fileName: exactName ? fileName : `${fileName}-${Moment().format("YYYY-MM-DD-HHmm")}.csv`,
    columnKeys: keys && keys.length ? keys : undefined,
    skipPinnedTop: true,
    skipPinnedBottom: true,
    processCellCallback: (p) => {
      const col = p.column.getColDef();
      if (typeof col.csvValue === "function") return safe(col.csvValue(p.node.data));
      if (Array.isArray(p.value)) return safe(p.value.join(", "));
      if (p.value && typeof p.value === "object") return safe(JSON.stringify(p.value));
      return safe(p.value);
    },
  });
}

// Legacy wrapper: toolbar (left: summary text, right: actions) + a raw
// <AgGridReact> child. New pages should use <DataGrid>.
export const GridCard = ({ toolbarLeft, toolbarRight, height = 520, children }) => (
  <div className="pa-grid">
    {(toolbarLeft || toolbarRight) && (
      <div className="pa-grid-toolbar">
        <div className="pa-muted">{toolbarLeft}</div>
        <div className="pa-grid-toolbar-actions">{toolbarRight}</div>
      </div>
    )}
    <div className="ag-theme-alpine" style={{ height, width: "100%" }}>
      {children}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* DataGrid                                                            */
/* ------------------------------------------------------------------ */

const readState = (key) => {
  try {
    const s = JSON.parse(localStorage.getItem(key) || "null");
    return Array.isArray(s) ? s : null;
  } catch (e) {
    return null;
  }
};
const writeState = (key, state) => {
  try {
    if (state) localStorage.setItem(key, JSON.stringify(state));
    else localStorage.removeItem(key);
  } catch (e) {
    // storage unavailable (private window): the layout just won't persist
  }
};

// Column events caused by the viewer (drag, resize handle, column / context
// menu, tool panel). Others ("flex" when the viewport is sized, "api" incl. our
// own restore, "gridInitializing", "sizeColumnsToFit", "gridOptionsChanged")
// must not save a layout the viewer never changed.
const USER_COLUMN_SOURCE = /^(ui|toolPanel|contextMenu|columnMenu)/;
const byViewer = (e) => Boolean(e && typeof e.source === "string" && USER_COLUMN_SOURCE.test(e.source));
// ag-grid 26's sortChanged event has no source, so a sort counts as the
// viewer's when it follows a click, tap or key press in the header or a column menu.
const HEADER_INPUT = ".ag-header, .ag-menu, .ag-popup, .ag-column-select, .ag-tool-panel-wrapper";
const HEADER_INPUT_WINDOW_MS = 1500;

function LoadingOverlay() {
  return (
    <div className="pa-grid-overlay" role="status">
      <span className="pa-spinner" aria-hidden="true" /> Loading…
    </div>
  );
}

// ag-grid 26 wrapper with the kit's look. See src/ui/README.md.
//   rowData, columnDefs (memoize them), loading
//   height (px, default 520) or autoHeight (grid grows with its rows)
//   pagination (default true), pageSize (50)
//   selection: "single" | "multiple" (adds a checkbox column unless
//     checkboxes={false}); onSelectionChange(rows)
//   exportName: shows "Export CSV" (file name prefix); exportOptions: the
//     exportCsv options for that button ({ includeHidden, formulaSafe, ... })
//   stateKey: remembers column order/width/sort/visibility per viewer
//   emptyTitle / emptyText / emptyAction: shown when there are no rows
//     (emptyAction: a button or link, e.g. "Clear filters")
//   toolbarLeft / toolbarRight: content for the bar above the grid
// Any other prop goes to <AgGridReact> (onRowClicked, getRowNodeId,
// pinnedBottomRowData, rowClassRules, ...). The ref exposes { api, columnApi,
// exportCsv(name?), resetColumns() }.
export const DataGrid = forwardRef(function DataGrid(
  {
    rowData,
    columnDefs,
    loading = false,
    height = 520,
    autoHeight = false,
    pagination = true,
    pageSize = 50,
    selection,
    checkboxes = true,
    onSelectionChange,
    exportName,
    exportOptions,
    stateKey,
    emptyTitle = "Nothing to show",
    emptyText = "No rows match these filters.",
    emptyAction,
    toolbarLeft,
    toolbarRight,
    className = "",
    onGridReady,
    defaultColDef,
    onColumnMoved,
    onColumnResized,
    onColumnVisible,
    onColumnPinned,
    onSortChanged,
    ...rest
  },
  ref
) {
  const apiRef = useRef(null);
  const colApiRef = useRef(null);
  const saveTimer = useRef(null);
  const headerInputAt = useRef(0);
  const [ready, setReady] = useState(false);
  const storageKey = stateKey ? `pa.grid.${stateKey}` : null;

  // The empty overlay is mounted by ag-grid once, so it reads its title, text
  // and action from a small store and re-renders in place (no remount, so a
  // focused action button keeps focus) whenever the grid's props change.
  const emptyStore = useRef(null);
  if (!emptyStore.current) emptyStore.current = { value: null, listeners: new Set() };
  emptyStore.current.value = { emptyTitle, emptyText, emptyAction };
  useEffect(() => {
    emptyStore.current.listeners.forEach((fn) => fn());
  });
  const EmptyOverlay = useMemo(
    () =>
      function EmptyOverlay() {
        const [, rerender] = useState(0);
        useEffect(() => {
          const store = emptyStore.current;
          const fn = () => rerender((n) => n + 1);
          store.listeners.add(fn);
          return () => store.listeners.delete(fn);
        }, []);
        const v = emptyStore.current.value;
        return (
          <div className="pa-grid-empty">
            <EmptyState compact title={v.emptyTitle} action={v.emptyAction}>
              {v.emptyText}
            </EmptyState>
          </div>
        );
      },
    []
  );

  const columns = useMemo(() => {
    if (!selection || !checkboxes) return columnDefs;
    const box = {
      colId: "__select",
      headerName: "",
      width: 48,
      minWidth: 48,
      maxWidth: 48,
      checkboxSelection: true,
      headerCheckboxSelection: selection === "multiple",
      headerCheckboxSelectionFilteredOnly: true,
      pinned: "left",
      lockPosition: true,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      type: "noCsv",
    };
    return [box, ...(columnDefs || [])];
  }, [columnDefs, selection, checkboxes]);

  const saveColumns = useCallback(() => {
    if (!storageKey || !colApiRef.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => colApiRef.current && writeState(storageKey, colApiRef.current.getColumnState()), 300);
  }, [storageKey]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const markHeaderInput = (e) => {
    if (storageKey && e.target && e.target.closest && e.target.closest(HEADER_INPUT)) headerInputAt.current = Date.now();
  };
  const sortByViewer = () => Date.now() - headerInputAt.current < HEADER_INPUT_WINDOW_MS;
  // Page handlers passed for these events still run.
  const chain = (own, page) => (e) => {
    own(e);
    if (page) page(e);
  };

  const handleReady = (params) => {
    apiRef.current = params.api;
    colApiRef.current = params.columnApi;
    const saved = storageKey && readState(storageKey);
    if (saved) params.columnApi.applyColumnState({ state: saved, applyOrder: true });
    setReady(true);
    if (onGridReady) onGridReady(params);
  };

  // Overlays follow `loading` and the row count.
  useEffect(() => {
    const api = apiRef.current;
    if (!ready || !api) return;
    if (loading) api.showLoadingOverlay();
    else if (!rowData || rowData.length === 0) api.showNoRowsOverlay();
    else api.hideOverlay();
  }, [ready, loading, rowData]);

  const resetColumns = () => {
    if (!colApiRef.current) return;
    colApiRef.current.resetColumnState();
    writeState(storageKey, null);
  };

  useImperativeHandle(ref, () => ({
    get api() {
      return apiRef.current;
    },
    get columnApi() {
      return colApiRef.current;
    },
    exportCsv: (name, options) => exportCsv(apiRef.current, name || exportName || "export", options || exportOptions),
    resetColumns,
  }));

  const rowCount = rowData ? rowData.length : 0;
  const showToolbar = toolbarLeft || toolbarRight || exportName || storageKey;

  return (
    <div className={`pa-grid ${autoHeight ? "pa-grid-auto" : ""} ${className}`}>
      {showToolbar && (
        <div className="pa-grid-toolbar">
          <div className="pa-grid-toolbar-left">{toolbarLeft !== undefined ? toolbarLeft : !loading && <span className="pa-muted">{count(rowCount)} rows</span>}</div>
          <div className="pa-grid-toolbar-actions">
            {toolbarRight}
            {storageKey && (
              <Button size="sm" variant="ghost" icon={<IconColumns size={16} stroke={1.8} />} onClick={resetColumns} title="Restore the default column order and widths">
                Reset columns
              </Button>
            )}
            {exportName && (
              <Button size="sm" icon={<IconDownload size={16} stroke={1.8} />} onClick={() => exportCsv(apiRef.current, exportName, exportOptions)} disabled={!rowCount}>
                Export CSV
              </Button>
            )}
          </div>
        </div>
      )}
      <div
        className="ag-theme-alpine"
        style={autoHeight ? { width: "100%" } : { height, width: "100%" }}
        onMouseDownCapture={markHeaderInput}
        onTouchStartCapture={markHeaderInput}
        onKeyDownCapture={markHeaderInput}
      >
        <AgGridReact
          {...gridDefaults}
          defaultColDef={{ ...gridDefaults.defaultColDef, ...defaultColDef }}
          rowData={rowData}
          columnDefs={columns}
          domLayout={autoHeight ? "autoHeight" : "normal"}
          pagination={pagination}
          paginationPageSize={pageSize}
          rowSelection={selection}
          suppressRowClickSelection={Boolean(selection && checkboxes)}
          onSelectionChanged={onSelectionChange ? (e) => onSelectionChange(e.api.getSelectedRows()) : undefined}
          loadingOverlayComponentFramework={LoadingOverlay}
          noRowsOverlayComponentFramework={EmptyOverlay}
          onColumnMoved={chain((e) => byViewer(e) && saveColumns(), onColumnMoved)}
          onColumnResized={chain((e) => e.finished && byViewer(e) && saveColumns(), onColumnResized)}
          onColumnVisible={chain((e) => byViewer(e) && saveColumns(), onColumnVisible)}
          onColumnPinned={chain((e) => byViewer(e) && saveColumns(), onColumnPinned)}
          onSortChanged={chain(() => sortByViewer() && saveColumns(), onSortChanged)}
          onGridReady={handleReady}
          {...rest}
        />
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ */
/* Timeline                                                            */
/* ------------------------------------------------------------------ */

// items: [{ key?, title, time?, tone?, meta?, body? }] newest first (or in
// the order you want them shown). tone: green | amber | red | blue | purple | gray.
export const Timeline = ({ items, emptyText = "No events yet", className = "" }) => {
  if (!items || !items.length) return <EmptyState compact title={emptyText} />;
  return (
    <ol className={`pa-timeline ${className}`}>
      {items.map((it, i) => (
        <li key={it.key !== undefined ? it.key : i} className={`pa-tl-${it.tone || "gray"}`}>
          <div className="pa-tl-head">
            <span className="pa-tl-title">{it.title}</span>
            {it.time && (
              <time className="pa-tl-time" dateTime={Moment(it.time).toISOString()} title={timeAgo(it.time)}>
                {dateTime(it.time)}
              </time>
            )}
          </div>
          {it.meta && <div className="pa-tl-meta">{it.meta}</div>}
          {it.body && <div className="pa-tl-body">{it.body}</div>}
        </li>
      ))}
    </ol>
  );
};

/* ------------------------------------------------------------------ */
/* MoneyText                                                           */
/* ------------------------------------------------------------------ */

// Formatted rupee amount with tabular figures.
//   value: rupees (number or decimal string); paise: value is integer paise
//   direction: "CR" | "DR" (forces the sign), sign: show "+" for positives
//   tone: "auto" (green +, red −; the default with sign/direction) or a tone name
//   compact: "₹ 1.25 L" (the exact amount goes in the tooltip)
export const MoneyText = ({ value, paise = false, direction, sign = false, tone, compact = false, strong = false, fallback = "—", className = "" }) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
    return <span className={`pa-money pa-muted ${className}`}>{fallback}</span>;
  }
  let n = Number(value);
  if (paise) n = n / 100;
  if (direction === "DR") n = -Math.abs(n);
  if (direction === "CR") n = Math.abs(n);
  const signed = sign || Boolean(direction);
  const t = tone === "auto" || (tone === undefined && signed) ? (n > 0 ? "green" : n < 0 ? "red" : null) : tone;
  return (
    <span className={`pa-money pa-num ${t ? "pa-money-" + t : ""} ${strong ? "pa-strong" : ""} ${className}`} title={compact ? inr(n, { sign: signed }) : undefined}>
      {inr(n, { compact, sign: signed })}
    </span>
  );
};
